import React, { useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link,
  Image,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEditorRef, useEditorState } from "@udecode/plate/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUploadButton } from "./elements/ImageUploadButton";
import { UnsplashImageSelector } from "./elements/UnsplashImageSelector";
import { transformMediaUrl, detectMediaType } from "./utils/media-utils";
import { TableDropdownMenu } from "./TableDropdownMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Toolbar for the Plate.js editor with formatting options.
 *
 * Provides buttons for text formatting, headings, lists, quotes, and media insertion.
 * The buttons are connected to the Plate.js editor API to perform the actual formatting operations.
 */
export const EditorToolbar: React.FC = () => {
  const editor = useEditorRef();
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isUnsplashDialogOpen, setIsUnsplashDialogOpen] = useState(false);

  // Media embed state
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaSource, setMediaSource] = useState("");
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);

  // Link state
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  // Get editor state to access selection
  const editorState = useEditorState();

  // Function to handle opening the link dialog
  const handleOpenLinkDialog = () => {
    if (editor) {
      // Get selected text if any
      let selectedText = "";

      // Check if there's a selection in the editor
      if (editorState.selection) {
        // Use the DOM selection as the most reliable way to get selected text
        const domSelection = window.getSelection();
        if (domSelection && domSelection.toString()) {
          selectedText = domSelection.toString();
        }
      }

      // Set the selected text in the link text field
      if (selectedText) {
        setLinkText(selectedText);
      }

      // Open the dialog
      setIsLinkDialogOpen(true);
    } else {
      setIsLinkDialogOpen(true);
    }
  };

  // Handler for basic formatting buttons
  const handleMarkClick = (format: string) => {
    if (!editor) return;

    // Handle mark formatting (bold, italic, underline)
    if (["bold", "italic", "underline"].includes(format)) {
      editor.tf.toggleMark(format);
    }
  };

  // Handler for heading and block formatting
  const handleBlockClick = (format: string) => {
    if (!editor) return;

    // Handle different block types
    editor.tf.toggleBlock(format);
  };

  // Handler for image insertion from URL
  const handleInsertImage = () => {
    if (editor && imageUrl) {
      // Insert image using Plate's transformation API
      editor.tf.insertNodes({
        type: "img",
        url: imageUrl,
        alt: imageAlt || "Image",
        children: [{ text: "" }],
      });

      // Reset the form fields and close the dialog
      setImageUrl("");
      setImageAlt("");
      setIsImageDialogOpen(false);
    }
  };

  // Handler for image upload
  const handleImageSelected = (url: string, fileName: string) => {
    if (editor) {
      // Insert image using Plate's transformation API
      editor.tf.insertNodes({
        type: "img",
        url: url,
        alt: fileName,
        children: [{ text: "" }],
      });
    }
  };

  // Handler for media embed insertion
  const handleInsertMedia = () => {
    if (editor && mediaUrl) {
      // Transform the URL to an embeddable format
      const embedUrl = transformMediaUrl(mediaUrl);

      // Detect the media source if not provided
      const source = mediaSource || detectMediaType(mediaUrl);

      // Insert media embed using Plate's transformation API
      editor.tf.insertNodes({
        type: "media_embed",
        url: embedUrl,
        source: source,
        children: [{ text: "" }],
      });

      setMediaUrl("");
      setMediaSource("");
      setIsMediaDialogOpen(false);
    }
  };

  // Handler for link insertion
  const handleInsertLink = () => {
    if (editor && linkUrl) {
      // Get the current selection text or use provided text/URL
      const textToUse = linkText || linkUrl;

      // Insert link using Plate's transformation API
      editor.tf.insertNodes({
        type: "a",
        url: linkUrl,
        children: [{ text: textToUse }],
      });

      // Reset the form fields and close the dialog
      setLinkUrl("");
      setLinkText("");
      setIsLinkDialogOpen(false);
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 p-2 flex flex-wrap gap-1 sticky top-0 z-50">
      <div className="flex space-x-1 mr-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleMarkClick("bold")}
          className="h-8 w-8 p-0"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleMarkClick("italic")}
          className="h-8 w-8 p-0"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleMarkClick("underline")}
          className="h-8 w-8 p-0"
        >
          <Underline className="h-4 w-4" />
        </Button>
      </div>

      <div className="border-r border-gray-300 h-8 mx-1" />

      <div className="flex space-x-1 mr-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBlockClick("h1")}
          className="h-8 w-8 p-0"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBlockClick("h2")}
          className="h-8 w-8 p-0"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBlockClick("h3")}
          className="h-8 w-8 p-0"
        >
          <Heading3 className="h-4 w-4" />
        </Button>
      </div>

      <div className="border-r border-gray-300 h-8 mx-1" />

      <div className="flex space-x-1 mr-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBlockClick("ul")}
          className="h-8 w-8 p-0"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBlockClick("ol")}
          className="h-8 w-8 p-0"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
      </div>

      <div className="border-r border-gray-300 h-8 mx-1" />

      <div className="flex space-x-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpenLinkDialog}
          className="h-8 w-8 p-0"
          title="Insert link"
        >
          <Link className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleBlockClick("blockquote")}
          className="h-8 w-8 p-0"
        >
          <Quote className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Insert image"
            >
              <Image className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => setIsImageDialogOpen(true)}>
              <Link className="h-4 w-4 mr-2" />
              Insert via URL
            </DropdownMenuItem>
            {/* <DropdownMenuItem onClick={() => setIsUnsplashDialogOpen(true)}>
              <ImagePlus className="h-4 w-4 mr-2" />
              Insert from Unsplash
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                // Use the existing ImageUploadButton's functionality
                document.getElementById("image-upload-input")?.click();
              }}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload from computer
            </DropdownMenuItem> */}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMediaDialogOpen(true)}
          className="h-8 w-8 p-0"
          title="Insert media embed (YouTube, Vimeo, etc.)"
        >
          <Video className="h-4 w-4" />
        </Button>

        {/* Hidden ImageUploadButton for functionality */}
        <div className="hidden">
          <ImageUploadButton
            id="image-upload-input"
            className="h-8 w-8 p-0"
            onImageSelected={handleImageSelected}
          />
        </div>
      </div>
      <div className="border-r border-gray-300 h-8 mx-1" />
      <TableDropdownMenu />

      {/* Image URL Dialog */}
      <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
            <DialogDescription>
              Enter the URL of the image you want to insert
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="image-url" className="text-right">
                URL
              </Label>
              <Input
                id="image-url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="col-span-3"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="image-alt" className="text-right">
                Alt Text
              </Label>
              <Input
                id="image-alt"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                className="col-span-3"
                placeholder="Image description"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleInsertImage} disabled={!imageUrl}>
              Insert
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsplash Image Dialog */}
      <Dialog
        open={isUnsplashDialogOpen}
        onOpenChange={setIsUnsplashDialogOpen}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Insert Image from Unsplash</DialogTitle>
            <DialogDescription>
              Search and select an image from Unsplash
            </DialogDescription>
          </DialogHeader>
          <UnsplashImageSelector
            onSelectImage={(url, alt) => {
              handleImageSelected(url, alt);
              setIsUnsplashDialogOpen(false);
            }}
            onClose={() => setIsUnsplashDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Media Embed Dialog */}
      <Dialog open={isMediaDialogOpen} onOpenChange={setIsMediaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Media Embed</DialogTitle>
            <DialogDescription>
              Enter the URL of the media you want to embed (YouTube, Vimeo,
              etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="media-url" className="text-right">
                URL
              </Label>
              <Input
                id="media-url"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                className="col-span-3"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="media-source" className="text-right">
                Source
              </Label>
              <Input
                id="media-source"
                value={mediaSource}
                onChange={(e) => setMediaSource(e.target.value)}
                className="col-span-3"
                placeholder="YouTube"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleInsertMedia} disabled={!mediaUrl}>
              Insert
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog
        open={isLinkDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Reset form when dialog is closed
            setLinkUrl("");
            setLinkText("");
          }
          setIsLinkDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
            <DialogDescription>
              Enter the URL and text for the link you want to insert
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="link-url" className="text-right">
                URL
              </Label>
              <Input
                id="link-url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="col-span-3"
                placeholder="https://example.com"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="link-text" className="text-right">
                Text
              </Label>
              <Input
                id="link-text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                className="col-span-3"
                placeholder="Link text (optional)"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleInsertLink} disabled={!linkUrl}>
              Insert
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
